/**
 * What you may do to a body that cannot stop you (M2-07).
 *
 * This is the module the down state exists for. `isDown` has been in `vitals.ts`
 * since M1-04 with a note saying M2-07 decides what others may do about it, and
 * these are the four answers: **staunch** a helpless body, **loot** it, **drag**
 * it, or **execute** it. Rescue and robbery through the same door, at range 1,
 * against the same predicate — which is the design claim. Everything dramatic
 * in the medicine milestone happens to this state, and everything frightening
 * about PvP happens around it.
 *
 * Three of the four are **channelled**: they take real seconds and any blow
 * ends them. That is what makes robbery a decision rather than a reflex — ten
 * seconds kneeling beside a body in the open is ten seconds you are not
 * watching the treeline, and the log broadcasts it, so it has witnesses.
 * `execute` is the exception and is instant, because a killing blow that took
 * ten seconds would be a mechanic that gets the executioner killed rather than
 * one that makes them choose. Its brake is a confirmation the *server* checks.
 *
 * Every verb is gated by one predicate, `helplessFault`, run twice: once when
 * the request arrives and again when the channel completes. Ten seconds is long
 * enough for the body to wake up, be dragged away, or die, and a channel that
 * trusted its opening check would let a player rob somebody who stood up nine
 * seconds ago.
 */
import { capabilityFault } from './affordance.js';
import { BURY_TICKS, DIG_TICKS, finishBury, finishDig } from './cauldron.js';
import { fumbleLine, learnMedicine, nothingToTreat, patientPossessive, workingBlind, } from './care.js';
import { chebyshev } from './actor.js';
import { parseBandage, parseCauterize, parseClean, parseExecute, parseLoot, parseStaunch, parseSuture, } from './actions.js';
import { deriveCapabilities } from './body.js';
import { isHelpless, kitOf } from './fighter.js';
import { BUILD_TICKS, BUTCHER_TICKS, COOK_TICKS, PREPARE_TICKS, fireAt, finishBuild, finishButcher, finishCook, finishPrepare, } from './camp.js';
import { FORAGE_TICKS, finishForage } from './forage.js';
import { CHOP_TICKS, finishChop } from './woodcraft.js';
import { ANTISEPTIC_TAG, DRESSING_TAG, dressingValue, effectOf, NO_EFFECT, POULTICE_TAG, } from './herbs.js';
import { createLore, isSpent, learnRemedy, removeItem } from './item.js';
import { ANTISEPTIC_FACTOR, BLEED_PER_SUCCESS, FUMBLE_BLEED, IMPROVISED_MATERIAL_MOD, rollTreatment, treatmentDice, WATER_CLEAN_FACTOR, waterQualityAt, } from './medicine.js';
import { strainOnExertion } from './recovery.js';
import { AMPUTATE_TICKS, EXTRACT_TICKS, finishAmputate, finishExtract, isSurgicalVerb, surgicalFault, } from './surgery.js';
import { TICKS_PER_SECOND } from './time.js';
import { die } from './vitals.js';
/* -------------------------------------------------------------------------- *
 * Timings
 * -------------------------------------------------------------------------- */
/**
 * Ten seconds to take one thing off a body — the ticket's number, and it is a
 * long time on purpose. Long enough that a third party can cross a clearing and
 * stop you, which is the only reason robbery is interesting.
 */
export const LOOT_TICKS = TICKS_PER_SECOND * 10;
/**
 * Field first aid: five seconds of pressure, and interruptible the whole time.
 *
 * The emergency stop — pressed, not dressed. Short, because it is the thing you
 * do with the wolves still circling; long enough that a blow can end it, which
 * is what keeps it a decision.
 */
export const STAUNCH_TICKS = TICKS_PER_SECOND * 5;
/** Binding a dressing on takes a little longer than pressing one down. */
export const BANDAGE_TICKS = TICKS_PER_SECOND * 6;
/** Wound care is deliberate work by the fire — tens of seconds, not five. */
export const CLEAN_TICKS = TICKS_PER_SECOND * 15;
export const SUTURE_TICKS = TICKS_PER_SECOND * 25;
/** The brutal option is the *fast* one: an iron, a scream, and it is done. */
export const CAUTERIZE_TICKS = TICKS_PER_SECOND * 8;
const CHANNEL_TICKS = {
    loot: LOOT_TICKS,
    extract: EXTRACT_TICKS,
    amputate: AMPUTATE_TICKS,
    bury: BURY_TICKS,
    dig: DIG_TICKS,
    staunch: STAUNCH_TICKS,
    bandage: BANDAGE_TICKS,
    clean: CLEAN_TICKS,
    suture: SUTURE_TICKS,
    cauterize: CAUTERIZE_TICKS,
    forage: FORAGE_TICKS,
    build: BUILD_TICKS,
    butcher: BUTCHER_TICKS,
    cook: COOK_TICKS,
    prepare: PREPARE_TICKS,
    chop: CHOP_TICKS,
};
/** The verbs of the medicine ladder — the ones `medicalFault` gates. */
const MEDICAL_VERBS = new Set(['staunch', 'bandage', 'clean', 'suture', 'cauterize']);
function isMedicalVerb(verb) {
    return MEDICAL_VERBS.has(verb);
}
/** Wound types a suture can close — a clean edge to pull together (AoD §10.2). */
const SUTURABLE_TYPES = new Set(['cut', 'bite']);
const SUTURE_MIN_LEVEL = 2;
const SUTURE_MAX_LEVEL = 3;
/* -------------------------------------------------------------------------- *
 * The gate
 * -------------------------------------------------------------------------- */
/** Is the body at this id one others may work on — senseless, or dead? */
function helpless(world, catalogue, actorId, tick) {
    const combatant = world.combat[actorId];
    return combatant !== undefined && isHelpless(catalogue, combatant, tick);
}
/**
 * Why this verb may not happen, or `null`.
 *
 * The `attackFault` pattern, and for the same reason: one predicate that both
 * the intent check and the resolution consult, so "may I" and "may I still"
 * cannot drift apart. Returns prose because the rejection reaches a player.
 */
export function helplessFault(world, catalogue, actor, targetId, tick, verb) {
    if (targetId === actor.id)
        return 'you cannot do that to yourself';
    const target = world.actors[targetId];
    if (!target)
        return 'there is nobody there';
    const combatant = world.combat[targetId];
    if (!combatant)
        return `${target.name} has no body to work on`;
    if (chebyshev(actor.x, actor.y, target.x, target.y) > 1) {
        return `${target.name} is out of reach`;
    }
    // The actor doing the work has to be in a state to do it — a man bleeding
    // out cannot rob the man who put him there.
    if (helpless(world, catalogue, actor.id, tick))
        return 'you are in no state to';
    if (!helpless(world, catalogue, targetId, tick)) {
        return `${target.name} is still on their feet`;
    }
    // What this verb asks of the body doing it (M3-04). Last, because "they are
    // still on their feet" is the more useful thing to be told when both are
    // true — the target's state is what the player can change by waiting.
    if (verb)
        return capabilityFault(catalogue, world.combat[actor.id], verb);
    return null;
}
/* -------------------------------------------------------------------------- *
 * The medicine gate (M4)
 * -------------------------------------------------------------------------- */
/**
 * Why a treatment may not happen, or `null`.
 *
 * Beside `helplessFault` rather than folded into it, because the two ask
 * opposite things of the target. The helpless verbs need a body that *cannot
 * stop you*; medicine needs a body that *needs the work* — and that body may be
 * the treater's own, the one case `helplessFault` refuses outright. So this
 * shares the reach and capability checks and parts ways on who may be worked on:
 * yourself always, anybody else only once they are down (the "down bodies +
 * self" scope).
 */
export function medicalFault(world, catalogue, actor, targetId, tick, verb) {
    const target = world.actors[targetId];
    if (!target)
        return 'there is nobody there';
    const patient = world.combat[targetId];
    if (!patient)
        return `${target.name} has no body to work on`;
    const treater = world.combat[actor.id];
    // A senseless or dead treater cannot do fine work, self-treatment included:
    // you cannot bind your own wound while you are unconscious from it.
    if (!treater || isHelpless(catalogue, treater, tick))
        return 'you are in no state to';
    if (targetId !== actor.id) {
        if (chebyshev(actor.x, actor.y, target.x, target.y) > 1)
            return `${target.name} is out of reach`;
        // Someone else's wound is only yours to work on once they are down. A
        // conscious ally who wants binding has to lie still first (M4-01's scope);
        // extending first aid to allies on their feet is deferred.
        if (!helpless(world, catalogue, targetId, tick))
            return `${target.name} is still on their feet`;
    }
    // What the verb asks of the treater's hands (M3-04) — the capability seam a
    // bandaged hand fails on `suture` but passes on `staunch`.
    const capFault = capabilityFault(catalogue, treater, verb);
    if (capFault)
        return capFault;
    // ...and something for the treatment to actually do, and the supplies to do
    // it with.
    return treatmentEligibility(world, catalogue, actor, patient, verb, tick);
}
/**
 * Is there a wound this verb can act on, and the wherewithal to act? Prose or
 * `null`. Split out of `medicalFault` because it is the half a balance pass and
 * a content pass both touch, and the reach/capability half is neither's.
 */
function treatmentEligibility(world, catalogue, actor, patient, verb, tick) {
    const wounds = patient.vitals.wounds;
    switch (verb) {
        case 'staunch':
        case 'bandage':
            return worstBleed(wounds) ? null : 'there is nothing bleeding to work on';
        case 'clean':
            return dirtiest(wounds) ? null : 'there is nothing here that needs cleaning';
        case 'suture': {
            const inventory = world.inventories[actor.id];
            if (!inventory || !findSupply(inventory, catalogue, 'suture', tick)) {
                return 'you have no needle and thread';
            }
            return suturable(wounds) ? null : 'there is no wound here a stitch would hold';
        }
        case 'cauterize':
            if (!fireAt(world, actor.x, actor.y, tick))
                return 'there is no fire to heat an iron';
            return worstBleed(wounds) ? null : 'there is nothing bleeding to work on';
        default:
            return null;
    }
}
/* -------------------------------------------------------------------------- *
 * Choosing the wound, and the supplies
 * -------------------------------------------------------------------------- */
/**
 * The wound a verb acts on: the one the player named, or — the frightened
 * bystander's reflex — the one that most wants the work. `part` is the optional
 * steer from the body diagram; without it, each verb falls back to its own worst
 * case.
 */
function pickWound(wounds, verb, part) {
    if (part !== null) {
        const onPart = wounds.filter((wound) => wound.part === part);
        return chooseWound(onPart, verb);
    }
    return chooseWound(wounds, verb);
}
function chooseWound(wounds, verb) {
    switch (verb) {
        case 'clean':
            return dirtiest(wounds);
        case 'suture':
            return suturable(wounds);
        default:
            return worstBleed(wounds);
    }
}
/** The wound that is killing them fastest. What a frightened bystander picks. */
function worstBleed(wounds) {
    let worst = null;
    for (const wound of wounds) {
        if (wound.bleedRate <= 0)
            continue;
        if (!worst || wound.bleedRate > worst.bleedRate)
            worst = wound;
    }
    return worst;
}
/** The wound with the most filth in it — the one washing helps most. */
function dirtiest(wounds) {
    let worst = null;
    for (const wound of wounds) {
        if (!wound.dirty || wound.state === 'healed')
            continue;
        if (!worst || wound.infectionMod > worst.infectionMod)
            worst = wound;
    }
    return worst;
}
/** A wound a stitch would close: a clean edge, level 2–3, not already shut. */
function suturable(wounds) {
    let best = null;
    for (const wound of wounds) {
        if (!SUTURABLE_TYPES.has(wound.type))
            continue;
        if (wound.level < SUTURE_MIN_LEVEL || wound.level > SUTURE_MAX_LEVEL)
            continue;
        if (wound.sutured || wound.cauterized || wound.state === 'healed')
            continue;
        if (!best || wound.bleedRate > best.bleedRate)
            best = wound;
    }
    return best;
}
/**
 * The first usable supply the treater is carrying with this tag, or `null`.
 *
 * *Usable* is the M4-03 half: a stack past its shelf life is skipped, so a
 * satchel of month-old poultices is a satchel with no poultices in it. That is
 * the acceptance criterion ("expired preparations do nothing") enforced at the
 * point of use rather than by a sweep that deletes people's things behind their
 * backs — the spent row stays in the pane, reading `spent`, which is how the
 * player learns that herbs turn.
 */
function findSupply(inventory, catalogue, tag, tick) {
    for (const stack of inventory.items) {
        if (stack.count <= 0)
            continue;
        const def = catalogue.item?.(stack.defId);
        if (!def?.tags.includes(tag))
            continue;
        if (isSpent(def, stack, tick))
            continue;
        return stack.defId;
    }
    return null;
}
/** Everything in the satchel that could be bound onto a wound. */
const DRESSING_TAGS = [POULTICE_TAG, DRESSING_TAG, 'bandage'];
/**
 * The dressing a bandage will spend: the best thing in the satchel, and what it
 * is worth (M4-03). `null` when there is nothing — the caller then improvises
 * from a torn tunic.
 *
 * Scored rather than ranked by tag, because with the pharmacopoeia in place the
 * ordering is genuinely a judgement between different goods: honey buys the
 * fever, moss buys absorbency, a yarrow poultice buys the roll. `dressingValue`
 * is where that judgement is made, once, in a function a balance pass can turn —
 * a chain of `if (poultice)` here would have frozen the ordering in a rule the
 * day content wanted to change it.
 *
 * Ties break on the item id, so two equally good dressings are spent in the same
 * order on every machine and in every replay.
 */
function pickDressing(inventory, catalogue, tick) {
    let best = null;
    for (const stack of inventory.items) {
        if (stack.count <= 0)
            continue;
        const def = catalogue.item?.(stack.defId);
        if (!def || !def.tags.some((tag) => DRESSING_TAGS.includes(tag)))
            continue;
        if (isSpent(def, stack, tick))
            continue;
        const effect = effectOf(catalogue, def.id);
        const score = dressingValue(effect);
        if (best === null || score > best.score || (score === best.score && def.id < best.defId)) {
            best = { defId: def.id, effect, score };
        }
    }
    return best === null ? null : { defId: best.defId, effect: best.effect };
}
/* -------------------------------------------------------------------------- *
 * Channels
 * -------------------------------------------------------------------------- */
/**
 * Open a channel, or replace the one in progress.
 *
 * Replacing rather than refusing is the roguelike answer: a player who starts
 * looting the shield when they meant the spear should not have to wait ten
 * seconds to correct themselves. What they lose is the progress, which is the
 * honest cost.
 */
function beginChannel(ctx, actor, combatant, verb, targetId, detail) {
    const channel = {
        verb,
        targetId,
        startedAt: ctx.tick,
        completesAt: ctx.tick + CHANNEL_TICKS[verb],
        fromX: actor.x,
        fromY: actor.y,
        detail,
    };
    combatant.channel = channel;
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, targetId],
        refs: [verb, 'begun', detail ?? ''],
        text: beginningLine(verb, actor, ctx.world.actors[targetId]?.name ?? 'someone', detail, targetId === actor.id),
        // Robbery has witnesses. So does rescue — a rescue nobody saw is a rescue
        // that cannot be remembered, and M5's gossip reads this line.
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
function beginningLine(verb, actor, targetName, detail, self) {
    // Whose wound it is changes the sentence: kneeling over a friend reads
    // differently from bowing over your own arm.
    const patient = self ? 'their own wound' : `${targetName}'s`;
    switch (verb) {
        case 'staunch':
            return self
                ? `${actor.name} claps a hand to the wound and bears down.`
                : `${actor.name} kneels over ${targetName} and presses down hard.`;
        case 'bandage':
            return `${actor.name} sets to binding ${patient}.`;
        case 'clean':
            return `${actor.name} sets to washing ${patient} out.`;
        case 'suture':
            return `${actor.name} threads a needle over ${patient}.`;
        case 'cauterize':
            return `${actor.name} lays an iron in the fire, ${self ? 'jaw set' : `over ${targetName}`}.`;
        case 'loot':
            return `${actor.name} starts going through ${targetName}'s ${detail ?? 'gear'}.`;
        default:
            return `${actor.name} starts going through ${targetName}'s things.`;
    }
}
/**
 * What giving up on each verb reads as.
 *
 * Written per verb rather than as one "stops what they were doing", because
 * the sentence is the *only* place a player learns the rule, and a generic one
 * teaches nothing. "Straightens up, the garlic still in the ground" says both
 * what was lost and that it is still there to come back to.
 */
const STOPPED = {
    forage: 'straightens up, leaving it in the ground.',
    staunch: 'takes their hands away.',
    bandage: 'leaves the dressing half-tied.',
    clean: 'leaves the wound half-washed.',
    suture: 'leaves the stitch half-drawn.',
    cauterize: 'takes the iron out of the fire.',
    extract: 'takes the knife out of the wound, and leaves what is in there in there.',
    bury: 'leaves the grave half-dug.',
    dig: 'leaves the grave alone, half-opened.',
    amputate: 'lowers the axe.',
    loot: 'leaves it where it lies.',
    build: 'leaves the work half-done.',
    butcher: 'leaves the carcass.',
    cook: 'leaves it off the fire.',
    prepare: 'leaves it half-made beside the fire.',
    chop: 'leaves the deadfall half-cleared.',
};
/**
 * Advance every open channel, and resolve the ones that are due.
 *
 * Table-driven over `world.combat` in a fixed order, like every other system
 * here, so the tick cannot depend on who was inserted first. Cancellation is
 * checked here rather than hooked into movement, for the same reason a swing
 * compares `fromX`/`fromY` instead: every future way of being moved cancels it
 * without anybody remembering to add a call.
 */
export const interactSystem = {
    name: 'interactions',
    phase: 'sim',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            const channel = combatant?.channel;
            if (!combatant || !actor || !channel)
                continue;
            // You moved, so it does not happen.
            //
            // This was silent until a playtest, on the argument that a cancelled
            // swing is silent and nothing had happened worth reporting. That was
            // wrong, and the way it was wrong is worth recording: a player pressed
            // `f`, read "kneels to gather the bed of garlic", walked two paces while
            // the six seconds ran, and got **nothing at all** — no herbs, no message,
            // no hint that standing still was the rule. Three times in a row. The
            // sim was behaving exactly as designed and the design was unlearnable.
            //
            // A cancelled swing can afford silence because a fight narrates itself
            // around it. A cancelled channel cannot: it is the only evidence the
            // player has that a rule exists.
            if (actor.x !== channel.fromX || actor.y !== channel.fromY) {
                combatant.channel = null;
                ctx.emit({
                    type: 'interacted',
                    actors: [actor.id],
                    refs: [channel.verb, 'stopped'],
                    text: `${actor.name} ${STOPPED[channel.verb]}`,
                    audience: 'witnesses',
                    at: { x: actor.x, y: actor.y },
                });
                continue;
            }
            if (ctx.tick < channel.completesAt)
                continue;
            combatant.channel = null;
            // The ground verbs — forage (M3-03) and the camp (M3-06) — work on a
            // tile or on a carcass rather than on a body, so each re-checks its own
            // fault and none of them reaches the helpless gate below. What they share
            // is the clock, the tile you must stay on, and the cancellation rule;
            // only the legality differs, which is exactly what `targetId: null` means.
            if (channel.targetId === null) {
                finishGroundVerb(ctx, actor, channel.verb);
                continue;
            }
            // A corpse is not an actor and has no gate to re-run beyond reach: it
            // cannot wake up, stand, or object (M4-07). What can change in ten
            // seconds is whether somebody else got there first, which `finishCorpseLoot`
            // discovers by finding the item already gone.
            const corpse = ctx.world.corpses[channel.targetId];
            if (corpse) {
                if (chebyshev(actor.x, actor.y, corpse.x, corpse.y) <= 1) {
                    finishCorpseLoot(ctx, actor, corpse, channel.detail);
                }
                continue;
            }
            // The second look. Long enough for the world to have changed its mind
            // about whether this was legal — a body woke up, a wound stopped bleeding
            // under the pressure already applied, the fire went out. The medicine
            // verbs consult their own gate, because "is this still worth doing" is a
            // different question for a treater than for a thief.
            const fault = isSurgicalVerb(channel.verb)
                ? surgicalFault(ctx.world, ctx.catalogue, actor, channel.targetId, ctx.tick, channel.verb, channel.detail)
                : isMedicalVerb(channel.verb)
                    ? medicalFault(ctx.world, ctx.catalogue, actor, channel.targetId, ctx.tick, channel.verb)
                    : helplessFault(ctx.world, ctx.catalogue, actor, channel.targetId, ctx.tick);
            if (fault !== null) {
                ctx.emit({
                    type: 'interacted',
                    actors: [actor.id, channel.targetId],
                    refs: [channel.verb, 'failed', fault],
                    text: `${actor.name} thinks better of it.`,
                    audience: 'witnesses',
                    at: { x: actor.x, y: actor.y },
                });
                continue;
            }
            completeChannel(ctx, actor, channel);
        }
    },
};
/**
 * The ground verbs that count as heavy labour (M4-04).
 *
 * Laying a fire and cutting up a stag are the two jobs in the camp that a man
 * with a half-healed thigh should think twice about. Foraging, cooking and
 * making up a poultice are not on the list, deliberately: convalescence has to
 * leave somebody *something* useful to do, and light-duty co-op is the thing
 * that emerges from exactly which verbs are on this list.
 */
const HEAVY_VERBS = new Set(['build', 'butcher', 'chop']);
/** Dispatch for the verbs that work on ground. */
function finishGroundVerb(ctx, actor, verb) {
    if (HEAVY_VERBS.has(verb))
        strainOnExertion(ctx, actor);
    switch (verb) {
        case 'bury':
            finishBury(ctx, actor);
            return;
        case 'dig':
            finishDig(ctx, actor);
            return;
        case 'build':
            finishBuild(ctx, actor);
            return;
        case 'butcher':
            finishButcher(ctx, actor);
            return;
        case 'cook':
            finishCook(ctx, actor);
            return;
        case 'prepare':
            finishPrepare(ctx, actor);
            return;
        case 'chop':
            finishChop(ctx, actor);
            return;
        default:
            finishForage(ctx, actor);
    }
}
function completeChannel(ctx, actor, channel) {
    // Ground verbs are dispatched before this is reached; a null target here
    // would be a channel with no verb to run.
    if (channel.targetId === null)
        return;
    const target = ctx.world.actors[channel.targetId];
    const patient = ctx.world.combat[channel.targetId];
    if (!target || !patient)
        return;
    switch (channel.verb) {
        case 'staunch':
            finishStaunch(ctx, actor, target, patient, channel.detail);
            return;
        case 'bandage':
            finishBandage(ctx, actor, target, patient, channel.detail);
            return;
        case 'clean':
            finishClean(ctx, actor, target, patient, channel.detail);
            return;
        case 'suture':
            finishSuture(ctx, actor, target, patient, channel.detail);
            return;
        case 'cauterize':
            finishCauterize(ctx, actor, target, patient, channel.detail);
            return;
        case 'extract':
            finishExtract(ctx, actor, target, patient, channel.detail);
            return;
        case 'amputate':
            finishAmputate(ctx, actor, target, patient, channel.detail);
            return;
        default:
            finishLoot(ctx, actor, target, patient, channel.detail);
    }
}
/* -------------------------------------------------------------------------- *
 * The medicine, resolved (M4)
 * -------------------------------------------------------------------------- */
/** The pain a hot iron adds, on top of stopping the bleed. Small, and it screams. */
const CAUTERY_PAIN = 3;
/** How far a cautery is heard — the stealth cost of the certain option. */
const CAUTERY_EARSHOT = 12;
/** What a botched stitch leaves festering: a needle-track of new risk. */
const SUTURE_FUMBLE_INFECTION = 0.15;
/** The die a clean water source is worth on the wash — the well earning it. */
const CLEAN_WATER_BONUS = {
    clean: 2,
    river: 0,
    bog: -1,
};
/* ---------------------------------------------------------------- staunch -- */
/**
 * Pressure, rolled (M4-01). Presses the bleed down by 3 a success; a fumble is
 * a slipped hand, and it makes the bleed *worse*. No material, no dressing, no
 * change of state — this is the emergency stop, not the field dressing that
 * `bandage` is.
 */
function finishStaunch(ctx, actor, target, patient, part) {
    const treater = ctx.world.combat[actor.id];
    const wound = pickWound(patient.vitals.wounds, 'staunch', part);
    if (!treater || !wound)
        return nothingToTreat(ctx, actor, target, 'staunch');
    // Yarrow, pressed in with the hand (M4-03). Spent whatever happens next, like
    // the dressing a bandage burns: a handful of leaves crushed into a wound is a
    // handful of leaves you no longer have, and the herb is the reason this is the
    // one first-aid verb worth carrying supplies for.
    const poultice = spendPoultice(ctx, actor);
    const self = target.id === actor.id;
    const dice = treatmentDice(treater, ctx.world.lore[actor.id], {
        self,
        dark: workingBlind(ctx, actor),
        materialMod: poultice.effect.dice ?? 0,
    });
    const roll = rollTreatment(ctx.rng('medicine'), dice);
    if (roll.fumble) {
        wound.bleedRate += FUMBLE_BLEED;
        return fumbleLine(ctx, actor, target, 'staunch', wound);
    }
    wound.bleedRate = Math.max(0, wound.bleedRate - BLEED_PER_SUCCESS * roll.successes);
    if (roll.successes > 0) {
        learnMedicine(ctx, actor);
        // Held the wound and watched the herb work: that is how a person learns what
        // yarrow is for, and it is the only way this game ever tells them.
        if (poultice.defId)
            learnHerb(ctx, actor, poultice.defId);
    }
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['staunch', 'done', wound.id, wound.part, poultice.defId ?? ''],
        text: wound.bleedRate === 0
            ? `The bleeding at ${patientPossessive(actor, target)} ${wound.zone} stops under ${actor.name}'s hands.`
            : roll.successes > 0
                ? `${actor.name} slows the bleeding at ${patientPossessive(actor, target)} ${wound.zone}.`
                : `${actor.name} presses hard, but the bleeding does not slow.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* --------------------------------------------------------------- bandage -- */
/**
 * The field dressing (M4-01). A material is spent — the best in the satchel, or
 * a torn tunic if there is none — and a wound bound to a stop becomes `treated`:
 * bleeding halted, infection rolls reduced but **not** cancelled, because a
 * field dressing over unwashed filth is a loan against the fever, not a payment.
 */
function finishBandage(ctx, actor, target, patient, part) {
    const treater = ctx.world.combat[actor.id];
    const wound = pickWound(patient.vitals.wounds, 'bandage', part);
    if (!treater || !wound)
        return nothingToTreat(ctx, actor, target, 'bandage');
    // Spend the dressing before the roll: a fumbled bandage still uses up the
    // cloth, and a torn tunic is torn whether or not the binding held.
    const material = spendDressing(ctx, actor, treater);
    const herb = material.effect;
    const self = target.id === actor.id;
    const roll = rollTreatment(ctx.rng('medicine'), treatmentDice(treater, ctx.world.lore[actor.id], {
        self,
        dark: workingBlind(ctx, actor),
        materialMod: material.mod + (herb.dice ?? 0),
    }));
    if (roll.fumble) {
        wound.bleedRate += FUMBLE_BLEED;
        return fumbleLine(ctx, actor, target, 'bandage', wound);
    }
    wound.bleedRate = Math.max(0, wound.bleedRate - BLEED_PER_SUCCESS * roll.successes);
    // What the *material* is worth, over what the hands achieved (M4-03). Applied
    // after the roll and only on a wound the roll did not close, so a herb is
    // always a modifier on a treatment and never a treatment of its own — press a
    // poultice onto a corpse's arm and nothing happens, because no roll happened.
    if (wound.bleedRate > 0) {
        wound.bleedRate = Math.max(0, wound.bleedRate - (herb.bleed ?? 0));
    }
    // Honey's half, moss's fifth: the dressing itself buys down what the wound is
    // carrying. It multiplies `infectionMod`, exactly as cleaning does, rather
    // than touching the state factor — the risk model (M1-05) is untouched and
    // what changed is the wound.
    if (herb.infection !== undefined)
        wound.infectionMod *= herb.infection;
    wound.dressedAt = ctx.tick;
    // Bound to a stop: a fresh wound becomes a treated one. Not upgraded past
    // that here — cleaning and healing (M4-02, M4-04) walk it the rest of the way.
    const bound = wound.bleedRate === 0;
    if (bound && wound.state === 'fresh')
        wound.state = 'treated';
    if (roll.successes > 0) {
        learnMedicine(ctx, actor);
        if (material.defId)
            learnHerb(ctx, actor, material.defId);
    }
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['bandage', 'done', wound.id, wound.part, material.refs],
        text: `${actor.name} binds ${patientPossessive(actor, target)} ${wound.zone}${material.clause}${bound ? ', and it holds' : ''}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/**
 * Take a dressing out of the satchel, or tear one off a back. Returns the die
 * modifier it is worth, and the phrases the log names it by. Mutates the world:
 * a dressing spent, or a garment sacrificed (M4-01's visible cost).
 */
function spendDressing(ctx, actor, treater) {
    const inventory = ctx.world.inventories[actor.id];
    const dressing = inventory ? pickDressing(inventory, ctx.catalogue, ctx.tick) : null;
    if (dressing && inventory) {
        removeItem(inventory, dressing.defId, 1);
        const name = ctx.catalogue.item?.(dressing.defId)?.name ?? 'a dressing';
        return {
            mod: 0,
            effect: dressing.effect,
            defId: dressing.defId,
            clause: ` with ${name}`,
            refs: dressing.defId,
        };
    }
    // No dressing in the satchel: improvise from worn clothing. A cloak or tunic
    // is armour worth nothing (`armourValue` 0), which is exactly the thing you
    // can tear into strips — mail is not. It comes off the treater's own back, so
    // the cost is theirs to pay, and it is visible in the log and in what they are
    // wearing afterwards. Failing that, bare hands and no material at all.
    const cloth = stripCloth(ctx.catalogue, treater);
    if (cloth) {
        return {
            mod: IMPROVISED_MATERIAL_MOD,
            effect: NO_EFFECT,
            defId: null,
            clause: ` with a strip torn from ${cloth}`,
            refs: 'improvised',
        };
    }
    return {
        mod: IMPROVISED_MATERIAL_MOD,
        effect: NO_EFFECT,
        defId: null,
        clause: ' with what is to hand',
        refs: 'bare',
    };
}
/**
 * Take a poultice out of the satchel for a staunch, or come away empty-handed.
 *
 * Only a `poultice` — not any dressing — because this is the verb where you are
 * *pressing something into* a wound rather than binding one up, and a strip of
 * linen pressed into a cut is a hand pressed into a cut. That is the honest
 * reading of AoD §10.1's yarrow modifier, and it is what keeps `staunch` the
 * verb you can always do with nothing at all.
 */
function spendPoultice(ctx, actor) {
    const inventory = ctx.world.inventories[actor.id];
    const defId = inventory ? findSupply(inventory, ctx.catalogue, POULTICE_TAG, ctx.tick) : null;
    if (!inventory || defId === null)
        return { effect: NO_EFFECT, defId: null };
    removeItem(inventory, defId, 1);
    return { effect: effectOf(ctx.catalogue, defId), defId };
}
/** Watch a herb work, and know it from now on (M4-03's discoverability). */
function learnHerb(ctx, actor, defId) {
    const lore = (ctx.world.lore[actor.id] ??= createLore());
    learnRemedy(lore, defId);
}
/** Tear a worthless-armour garment off the treater. Returns its name, or null. */
function stripCloth(catalogue, treater) {
    for (const id of treater.loadout.armourIds) {
        const piece = catalogue.armour(id);
        if (piece && piece.armourValue === 0) {
            treater.loadout = {
                ...treater.loadout,
                armourIds: treater.loadout.armourIds.filter((worn) => worn !== id),
            };
            return piece.name;
        }
    }
    return null;
}
/* ----------------------------------------------------------------- clean -- */
/**
 * Wash a wound out (M4-02). Clears the `dirty` flag and drops the wound's
 * infection modifier by a factor set by the **water to hand** — the well earns
 * its place here — and further again if there is honey or garlic to wash it
 * with. A fumble is dirty water gone in: nothing gained, and the log says so.
 */
function finishClean(ctx, actor, target, patient, part) {
    const treater = ctx.world.combat[actor.id];
    const wound = pickWound(patient.vitals.wounds, 'clean', part);
    if (!treater || !wound)
        return nothingToTreat(ctx, actor, target, 'clean');
    const quality = waterQualityAt(ctx.world.map, actor.x, actor.y);
    const inventory = ctx.world.inventories[actor.id];
    const antiseptic = inventory
        ? findSupply(inventory, ctx.catalogue, ANTISEPTIC_TAG, ctx.tick)
        : null;
    // Garlic's die, honey's half (M4-03). Read before the roll because the wash is
    // made up before it goes in.
    const wash = antiseptic ? effectOf(ctx.catalogue, antiseptic) : NO_EFFECT;
    const roll = rollTreatment(ctx.rng('medicine'), treatmentDice(treater, ctx.world.lore[actor.id], {
        self: target.id === actor.id,
        dark: workingBlind(ctx, actor),
        materialMod: wash.dice ?? 0,
        bonus: CLEAN_WATER_BONUS[quality],
    }));
    if (roll.fumble) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, target.id],
            refs: ['clean', 'fouled', wound.id, quality],
            text: `${actor.name} washes ${patientPossessive(actor, target)} ${wound.zone}, but the water does more harm than good.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    wound.dirty = false;
    let factor = WATER_CLEAN_FACTOR[quality];
    if (antiseptic && inventory) {
        removeItem(inventory, antiseptic, 1);
        // The herb's own number, or the flat half a tagged antiseptic is worth in a
        // world with no pharmacopoeia loaded. Honey is the premium (0.5), garlic the
        // cheap and effective one (0.7) — the ordering the ticket asks for, stated
        // where a balance pass can see both at once.
        factor *= wash.infection ?? ANTISEPTIC_FACTOR;
        learnHerb(ctx, actor, antiseptic);
    }
    wound.infectionMod *= factor;
    wound.dressedAt = ctx.tick;
    learnMedicine(ctx, actor);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['clean', 'done', wound.id, quality, antiseptic ?? ''],
        text: `${actor.name} washes ${patientPossessive(actor, target)} ${wound.zone} clean${antiseptic ? ` with ${ctx.catalogue.item?.(antiseptic)?.name ?? 'an antiseptic'}` : ''}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* ---------------------------------------------------------------- suture -- */
/**
 * Sew a cut shut (M4-02). Needs a needle and a whole steady hand; closes a clean
 * level 2–3 cut or bite for good — bleed to nothing, `sutured` set, healing time
 * halved (M4-04). A partial close (no successes) is a stitch that did not hold
 * and can be redone; a fumble drives new filth in.
 */
function finishSuture(ctx, actor, target, patient, part) {
    const treater = ctx.world.combat[actor.id];
    const wound = pickWound(patient.vitals.wounds, 'suture', part);
    if (!treater || !wound)
        return nothingToTreat(ctx, actor, target, 'suture');
    // Fine work rolls the wit pool *plus* the hand: a needle in a good hand is
    // steadier than one in a shaking one, which is the whole point of the fineWork
    // gate on the verb.
    const kit = kitOf(ctx.catalogue, treater);
    const fineWork = kit ? deriveCapabilities(kit.plan, treater.vitals.body).fineWork : 0;
    const roll = rollTreatment(ctx.rng('medicine'), treatmentDice(treater, ctx.world.lore[actor.id], {
        self: target.id === actor.id,
        dark: workingBlind(ctx, actor),
        materialMod: 0,
        bonus: Math.round(fineWork * 2),
    }));
    if (roll.fumble) {
        wound.infectionMod += SUTURE_FUMBLE_INFECTION;
        return fumbleLine(ctx, actor, target, 'suture', wound);
    }
    if (roll.successes === 0) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, target.id],
            refs: ['suture', 'partial', wound.id],
            text: `${actor.name}'s stitches will not sit true; the wound gapes yet.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    wound.bleedRate = 0;
    wound.sutured = true;
    wound.dressedAt = ctx.tick;
    if (wound.state === 'fresh')
        wound.state = 'treated';
    learnMedicine(ctx, actor);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['suture', 'done', wound.id, wound.part],
        text: `${actor.name} draws ${patientPossessive(actor, target)} ${wound.zone} closed with neat stitches.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* ------------------------------------------------------------- cauterize -- */
/**
 * Burn a wound shut (M4-02). The brutal certainty: it *always* closes the
 * bleed, and it always costs — a burn on the part, a pain spike, and a scream
 * heard well beyond the fire (the stealth cost of the sure thing). No roll,
 * because the whole point of the hot iron is that it does not fail; the price is
 * the price whether the hand that held it was skilled or not.
 */
function finishCauterize(ctx, actor, target, patient, part) {
    const treater = ctx.world.combat[actor.id];
    const wound = pickWound(patient.vitals.wounds, 'cauterize', part);
    if (!treater || !wound)
        return nothingToTreat(ctx, actor, target, 'cauterize');
    // The fire may have gone out in the seconds it took to heat the iron.
    if (!fireAt(ctx.world, actor.x, actor.y, ctx.tick)) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, target.id],
            refs: ['cauterize', 'no-fire', wound.id],
            text: `The fire has died, and the iron with it.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    wound.bleedRate = 0;
    wound.cauterized = true;
    wound.pain += CAUTERY_PAIN;
    wound.dressedAt = ctx.tick;
    if (wound.state === 'fresh')
        wound.state = 'treated';
    learnMedicine(ctx, actor);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['cauterize', 'done', wound.id, wound.part],
        // Earshot, not witnesses: the scream is the cost, and the whole valley
        // within a dozen tiles hears where you are and what you are doing.
        text: target.id === actor.id
            ? `${actor.name} lays the iron to the wound and screams through their teeth. The bleeding stops.`
            : `${actor.name} lays the iron to ${target.name}'s ${wound.zone}. ${target.name} screams. The bleeding stops.`,
        audience: 'earshot',
        range: CAUTERY_EARSHOT,
        at: { x: actor.x, y: actor.y },
    });
}
/* ------------------------------------------------------------------- loot -- */
/**
 * Everything on a body that could be taken.
 *
 * The loadout *is* the gear until there is an item system — see the note in
 * `ecology.ts` about not faking butchery. Robbing a man of his spear and his
 * mail is completely real; robbing him of a purse is not yet a thing the world
 * can model, and inventing a bag to hold nothing would be the worse lie.
 */
export function lootable(loadout) {
    return [loadout.weaponId, ...loadout.armourIds].filter((id) => id.length > 0);
}
/** Move one piece of kit from one loadout to another. */
function finishLoot(ctx, actor, target, victim, itemId) {
    const thief = ctx.world.combat[actor.id];
    if (!thief || itemId === null)
        return;
    const taken = takeFrom(victim, itemId, ctx.catalogue);
    if (!taken) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, target.id],
            refs: ['loot', 'gone', itemId],
            text: `${target.name} does not have it.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    thief.loadout = give(thief.loadout, itemId, taken, ctx.catalogue);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['loot', 'taken', itemId],
        text: `${actor.name} takes ${target.name}'s ${nameOf(ctx.catalogue, itemId) ?? itemId}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/**
 * Rob a body that has stopped being one (M4-07).
 *
 * The corpse-run is the entire cost of the cauldron: you come back naked at the
 * Pair Dadeni and your gear is still on the thing lying where you died. It has
 * to be *takeable*, and by anybody — a friend fetching it for you and a stranger
 * helping themselves are the same ten seconds and the same broadcast line, which
 * is what makes leaving a body unguarded a decision.
 *
 * Shares `takeFrom` and `give` with the living version, so a robbery is a
 * robbery whichever side of death it happens on.
 */
function finishCorpseLoot(ctx, actor, corpse, itemId) {
    const thief = ctx.world.combat[actor.id];
    if (!thief || itemId === null)
        return;
    const holder = {
        get loadout() {
            return corpse.loadout;
        },
        set loadout(next) {
            corpse.loadout = next;
        },
    };
    const taken = takeFrom(holder, itemId, ctx.catalogue);
    if (!taken)
        return;
    thief.loadout = give(thief.loadout, itemId, taken, ctx.catalogue);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, corpse.ownerId],
        refs: ['loot', 'taken', itemId, 'corpse'],
        text: `${actor.name} takes ${nameOf(ctx.catalogue, itemId) ?? itemId} off ${corpse.name}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
function takeFrom(victim, itemId, catalogue) {
    const { loadout } = victim;
    if (loadout.weaponId === itemId) {
        // Disarmed, not disembodied. A loadout with no weapon id resolves no
        // `CombatKit` at all — `kitOf` returns null — and a fighter with no kit
        // cannot be *hit*, which would have made robbing a man's seax the cheapest
        // way to make him invulnerable.
        //
        // So the weapon slot falls back to whatever the catalogue considers a
        // default, which in the shipped content is `fists`. That is the honest
        // answer anyway: you took his blade, he still has his hands.
        victim.loadout = { ...loadout, weaponId: catalogue.defaultLoadout?.weaponId ?? '' };
        return 'weapon';
    }
    const index = loadout.armourIds.indexOf(itemId);
    if (index === -1)
        return null;
    const armourIds = loadout.armourIds.filter((_, i) => i !== index);
    victim.loadout = { ...loadout, armourIds };
    return 'armour';
}
/**
 * Add taken kit to the looter.
 *
 * A stolen weapon is *carried*, not wielded — it goes nowhere if their hands
 * are full, because swapping your seax for a dead man's axe mid-fight is a
 * decision the game should make you take, and there is no place to put a
 * carried weapon until the item system exists. So: it arms the empty-handed and
 * is otherwise left on the ground with the body, which is honest about what the
 * world can currently represent.
 */
function give(loadout, itemId, slot, catalogue) {
    if (slot === 'armour') {
        if (loadout.armourIds.includes(itemId))
            return loadout;
        return { ...loadout, armourIds: [...loadout.armourIds, itemId] };
    }
    // Empty hands, or hands holding nothing better than the default (fists).
    // Anything else stays on the ground with the body: swapping your own seax
    // for a dead man's axe is a decision the game should make you take, and
    // there is nowhere to *carry* a second weapon until items exist.
    const empty = loadout.weaponId.length === 0 || loadout.weaponId === catalogue.defaultLoadout?.weaponId;
    return empty ? { ...loadout, weaponId: itemId } : loadout;
}
function nameOf(catalogue, itemId) {
    return catalogue.weapon(itemId)?.name ?? catalogue.armour(itemId)?.name ?? null;
}
/* -------------------------------------------------------------------------- *
 * Handlers
 * -------------------------------------------------------------------------- */
/** The opening check every verb shares: the actor exists, and the gate passes. */
function openingFault(ctx, actorId, targetId, verb) {
    const actor = ctx.world.actors[actorId];
    if (!actor)
        return `${actorId} is not in the world`;
    if (!ctx.world.combat[actorId])
        return `${actorId} has no hands to do it with`;
    return helplessFault(ctx.world, ctx.catalogue, actor, targetId, ctx.tick, verb);
}
/**
 * The medicine ladder's handlers, which differ only in their verb.
 *
 * A factory rather than five near-identical objects, because they genuinely do
 * one thing: resolve the target (the treater themselves when the payload names
 * nobody — the self-treatment case), gate it through `medicalFault`, and open a
 * channel whose `detail` carries the chosen part so completion can honour it.
 * A body diagram's part steer rides in on `payload.part`; without one, the
 * finisher falls back to the worst wound.
 */
function medicalHandler(verb, parse) {
    return {
        type: verb,
        parse,
        check(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            if (!actor)
                return `${intent.actorId} is not in the world`;
            if (!ctx.world.combat[intent.actorId])
                return `${intent.actorId} has no hands to do it with`;
            const targetId = intent.payload.targetId ?? intent.actorId;
            return medicalFault(ctx.world, ctx.catalogue, actor, targetId, ctx.tick, verb);
        },
        apply(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            const combatant = ctx.world.combat[intent.actorId];
            if (!actor || !combatant)
                return;
            const targetId = intent.payload.targetId ?? intent.actorId;
            beginChannel(ctx, actor, combatant, verb, targetId, intent.payload.part ?? null);
        },
    };
}
export const staunchHandler = medicalHandler('staunch', parseStaunch);
export const bandageHandler = medicalHandler('bandage', parseBandage);
export const cleanHandler = medicalHandler('clean', parseClean);
export const sutureHandler = medicalHandler('suture', parseSuture);
export const cauterizeHandler = medicalHandler('cauterize', parseCauterize);
export const lootHandler = {
    type: 'loot',
    parse: parseLoot,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return `${intent.actorId} is not in the world`;
        // Robbing the dead (M4-07). A corpse has no `helplessFault` to run — it is
        // not an actor and never stands up — so the gate is reach, hands, and
        // whether the thing is still on it.
        const corpse = ctx.world.corpses[intent.payload.targetId];
        if (corpse) {
            if (chebyshev(actor.x, actor.y, corpse.x, corpse.y) > 1)
                return `${corpse.name} is out of reach`;
            const capFault = capabilityFault(ctx.catalogue, ctx.world.combat[intent.actorId], 'loot');
            if (capFault)
                return capFault;
            return lootable(corpse.loadout).includes(intent.payload.itemId)
                ? null
                : 'there is nothing of the sort on the body';
        }
        const fault = openingFault(ctx, intent.actorId, intent.payload.targetId, 'loot');
        if (fault)
            return fault;
        const victim = ctx.world.combat[intent.payload.targetId];
        if (!victim || !lootable(victim.loadout).includes(intent.payload.itemId)) {
            return 'they do not have that';
        }
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = ctx.world.combat[intent.actorId];
        if (!actor || !combatant)
            return;
        beginChannel(ctx, actor, combatant, 'loot', intent.payload.targetId, intent.payload.itemId);
    },
};
/**
 * The killing blow.
 *
 * Not channelled, and not narrated gently. The confirmation is enforced in
 * `parseExecute` rather than trusted to the client's menu, because a rule the
 * server does not check is a rule that lasts until somebody writes their own
 * client — and this one is the difference between a scar and a grave.
 */
export const executeHandler = {
    type: 'execute',
    parse: parseExecute,
    check: (intent, ctx) => openingFault(ctx, intent.actorId, intent.payload.targetId, 'execute'),
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const target = ctx.world.actors[intent.payload.targetId];
        const victim = ctx.world.combat[intent.payload.targetId];
        if (!actor || !target || !victim || victim.vitals.dead)
            return;
        die(victim.vitals, 'execution');
        ctx.emit({
            type: 'died',
            // Victim first, then whoever did it. This was the one `died` in the game
            // emitted the other way round, which read to anything indexing the tuple
            // — M5-01's chronicle, first — as the executioner having died at the
            // hands of the person lying at their feet.
            actors: [target.id, actor.id],
            refs: ['execution'],
            text: `${actor.name} opens ${target.name}'s throat where they lie.`,
            audience: 'witnesses',
            at: { x: target.x, y: target.y },
        });
    },
};
export const INTERACT_INTENTS = [
    staunchHandler,
    bandageHandler,
    cleanHandler,
    sutureHandler,
    cauterizeHandler,
    lootHandler,
    executeHandler,
];
//# sourceMappingURL=interact.js.map